"use client";

import { useEffect, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import Link from "next/link";
import "./leadership.css";

/* All styling lives in leadership.css.
   Images public/assets/images/ se serve hoti hain (import nahi, seedha URL path).
   encodeURI se spaces / & wale file names safe ho jaate hain. */
const IMG = "/assets/images";
const img = (file: string) => encodeURI(`${IMG}/${file}`);

const imgHero = img("leadership-hero 1.jpg");
const imgCta = img("leadership-cta.jpg");

const imgCustomers = img("Customers.jpg");
const imgMarkets = img("Markets-&-Industries.jpg");
const imgTransformation = img("Transformation-&-Capability.jpg");
const imgPeople = img("People-&-Culture.jpg");
const imgCorporate = img("Corporate-Leadership.jpg");

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" },
    );
    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
}

const delay = (i: number, step = 80, max = 400): CSSProperties =>
  ({ "--ld-delay": `${Math.min(i * step, max)}ms` }) as CSSProperties;

type IconKey = "people" | "badge" | "gear" | "book" | "bars" | "arrow";

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    people: (
      <>
        <circle cx="12" cy="7" r="2.8" />
        <circle cx="5" cy="9" r="2.2" />
        <circle cx="19" cy="9" r="2.2" />
        <path d="M7 19c.4-3.2 2.3-4.8 5-4.8s4.6 1.6 5 4.8M1.8 17c.3-2.4 1.6-3.6 3.4-3.6M22.2 17c-.3-2.4-1.6-3.6-3.4-3.6" />
      </>
    ),
    badge: (
      <>
        <path d="M4 9h16v11H4zM9 9V6a3 3 0 0 1 6 0v3" />
        <circle cx="12" cy="14" r="2" />
        <path d="M9 19c.3-1.7 1.4-2.5 3-2.5s2.7.8 3 2.5" />
      </>
    ),
    gear: (
      <>
        <circle cx="12" cy="12" r="3.2" />
        <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1" />
        <circle cx="12" cy="12" r="7" />
      </>
    ),
    book: (
      <>
        <path d="M2.5 5.5C5 4.5 8.5 4.5 12 6.5c3.5-2 7-2 9.5-1v13c-2.5-1-6-1-9.5 1-3.5-2-7-2-9.5-1z" />
        <path d="M12 6.5v13" />
      </>
    ),
    bars: <path d="M5 20V12M11 20V4M17 20V9M3 20h18" />,
    arrow: <path d="M5 12h14m-6-6 6 6-6 6" />,
  };
  return (
    <svg
      className="ld-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const pillars: { icon: IconKey; title: string; text: string }[] = [
  {
    icon: "people",
    title: "Customer Ownership",
    text: "Understand what matters and stay accountable",
  },
  {
    icon: "badge",
    title: "People Leadership",
    text: "Build stronger professionals and future leaders",
  },
  {
    icon: "gear",
    title: "Capability Leadership",
    text: "Deepen expertise across industries and technologies",
  },
  {
    icon: "book",
    title: "Knowledge Leadership",
    text: "Turn experience into reusable knowledge",
  },
  {
    icon: "bars",
    title: "Outcome Ownership",
    text: "Keep business outcomes ahead of technology activity",
  },
];

const founder = {
  name: "Manuj Gupta",
  role: "Founder, Orane Consulting & Axentia Academy",
  image: "/assets/images/Manuj-gupta.jpg",
  linkedin: "https://www.linkedin.com/in/manuj123/",
  text: "With 25+ years of enterprise consulting experience, Manuj Gupta leads Orane Consulting, a trusted SAP Gold Partner. He founded Axentia Academy with a deep understanding of what enterprises need from talent today: strong fundamentals, practical skills and the ability to apply knowledge in the real world. His experience across enterprise technology and transformation continues to shape the Academy’s approach to career-ready learning.",
  facts: [
    { value: "25+", label: "Years of enterprise consulting" },
    { value: "SAP Gold", logo: "/assets/images/sap-gold-partner.jpg" },
    { value: "Axentia", logo: "/assets/images/axentia-logo.png" },
  ],
};

const areas = [
  {
    title: "Customers",
    text: "Long-term relationships and real business value.",
    image: imgCustomers,
    href: "/customers",
  },
  {
    title: "Markets & Industries",
    text: "India, Africa, Middle East and beyond.",
    image: imgMarkets,
    href: "/industries",
  },
  {
    title: "Transformation & Capability",
    text: "Enterprise systems, data, AI and industry expertise.",
    image: imgTransformation,
    href: "/whatwedo",
  },
  {
    title: "People & Culture",
    text: "Learning, growth and leadership development.",
    image: imgPeople,
    href: "/about/our-people",
  },
  {
    title: "Corporate Leadership",
    text: "Finance, HR, governance and strategic enablement.",
    image: imgCorporate,
    href: "/about/responsible-business",
  },
];

/* ============================== PAGE ============================== */

export default function LeadershipPage() {
  const ref = useScrollReveal();

  return (
    <div className="ld-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="ld-hero" data-purpose="hero-section" id="top">
          <div
            className="ld-hero-bg"
            style={{ backgroundImage: `url("${imgHero}")` }}
          ></div>
          <div className="ld-hero-overlay"></div>

          <div className="ld-hero-layout">
            <div className="ld-hero-copy">
              <h1 className="ld-hero-title" data-reveal style={delay(0)}>
                Leadership
              </h1>

              <p className="ld-hero-subtitle" data-reveal style={delay(1, 100)}>
                <span>Means Owning</span>
                <span>the Outcome.</span>
              </p>

              <p className="ld-hero-text" data-reveal style={delay(2, 100)}>
                Our leaders bring together business understanding, industry
                expertise, enterprise technology experience and a commitment to
                create lasting value for our customers, people and markets.
              </p>

              <div
                className="ld-actions ld-hero-actions"
                data-reveal
                style={delay(3, 100)}
              >
                <Link
                  href="#our-leadership"
                  className="fp-buttons fp-btn-primary"
                >
                  Meet Our Leadership <span>→</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ============================= PILLARS ============================= */}
        <section className="ld-strip-section" data-purpose="pillars">
          <div className="op-container">
            <div className="ld-strip" data-reveal>
              {pillars.map((p) => (
                <div className="ld-pillar" key={p.title}>
                  <span className="ld-pillar-icon">
                    <Icon name={p.icon} />
                  </span>
                  <div className="ld-pillar-copy">
                    <h3>{p.title}</h3>
                    <p>{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================= FOUNDER ============================= */}
        <section
          className="op-section"
          data-purpose="founder"
          id="our-leadership"
        >
          <div className="op-container">
            <div className="ld-founder">
              <div className="ld-founder-photo" data-reveal>
                <div className="ld-founder-frame">
                  <img
                    src={founder.image}
                    alt={`${founder.name}, ${founder.role}`}
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="ld-founder-copy">
                <div className="sec-badge" data-reveal>
                  Our Founder
                </div>
                <div className="ld-founder-namerow">
                  <h2
                    className="sec-title ld-founder-name"
                    data-reveal
                    style={delay(1, 100)}
                  >
                    {founder.name}
                  </h2>
                  <a
                    className="ld-in ld-founder-in"
                    href={founder.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${founder.name} on LinkedIn`}
                    data-reveal
                    style={delay(1, 100)}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452z" />
                    </svg>
                  </a>
                </div>
                <p
                  className="ld-founder-role"
                  data-reveal
                  style={delay(2, 100)}
                >
                  {founder.role}
                </p>
                <p
                  className="ld-founder-text"
                  data-reveal
                  style={delay(3, 100)}
                >
                  {founder.text}
                </p>

                <div
                  className="ld-founder-facts"
                  data-reveal
                  style={delay(4, 100)}
                >
                  {founder.facts.map((f) => (
                    <div className="ld-fact" key={f.value}>
                      {"logo" in f && f.logo ? (
                        <img
                          className="ld-fact-logo"
                          src={f.logo}
                          alt={f.value}
                          loading="lazy"
                        />
                      ) : (
                        <strong>{f.value}</strong>
                      )}
                      {f.label && <span>{f.label}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================= AREAS ============================= */}
        <section className="op-section" data-purpose="leadership-areas">
          <div className="op-container">
            <div className="sec-head" data-reveal>
              <div className="sec-badge">Where We Lead</div>
              <h2 className="sec-title">Leadership Across What Matters</h2>
              <p className="sec-sub">
                Our leaders work together across customers, markets, industries,
                capabilities and corporate functions.
              </p>
            </div>

            <div className="ld-area-grid">
              {areas.map((a, i) => (
                <Link
                  href={a.href}
                  className="ld-area"
                  key={a.title}
                  data-reveal
                  style={delay(i, 90)}
                >
                  <img src={a.image} alt="" aria-hidden="true" loading="lazy" />
                  <div className="ld-area-body">
                    <div>
                      <h3>{a.title}</h3>
                      <p>{a.text}</p>
                    </div>
                    <span className="ld-area-go" aria-hidden="true">
                      <Icon name="arrow" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ========================= FINAL CTA ================================ */}
        <section
          className="ld-cta"
          data-purpose="final-call-to-action"
          id="contact"
        >
          <img className="ld-cta-bg" src={imgCta} alt="" aria-hidden="true" />
          <div className="ld-cta-copy">
            <h2 data-reveal>Different Leaders. A Common Purpose.</h2>
            <p data-reveal style={delay(1, 120)}>
              Stronger customers. Stronger people. A more capable Orane. Real
              progress. Together.
            </p>
            <div className="ld-actions" data-reveal style={delay(2, 120)}>
              <Link
                href="/tellusyourproblem#tell-details"
                className="fp-buttons fp-btn-primary"
              >
                Talk to Our Leadership Team <span>→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
